<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { CriteriaInstance } from '~/types/Checklist';
import type { EvaluationRelevantInstance } from '~/types/EvaluationResults';

const props = defineProps<{
  criteriaInstance: CriteriaInstance;
  targetKind: 'criterion' | 'instance';
  targetUid: string;
  targetLabel: string;
  rootInstance?: EvaluationRelevantInstance | null;
  candidates?: EvaluationRelevantInstance[];
}>();
const emit = defineEmits<{ saved: [] }>();
const documentId = inject<string>('documentId')!;
const { revision } = storeToRefs(useSubmittedDocumentStore(documentId));
const { evaluationResults } = storeToRefs(useResultsForCriteriaIdStore(documentId, props.criteriaInstance));
const checklistStore = useDocumentChecklistStore(documentId);
const metricId = computed(() => checklistStore.getCriteriaFromId(props.criteriaInstance.checklistCriteriaId!)?.evaluationMetricId);
const selectedIndex = ref(props.candidates?.length === 1 ? '0' : '');
const selectedInstance = computed(() => props.rootInstance ?? (selectedIndex.value ? props.candidates?.[Number(selectedIndex.value)] : undefined));
const note = ref('');
const saving = ref(false);
const error = ref('');

const save = async () => {
  if (saving.value) return;
  const result = evaluationResults.value;
  const instance = selectedInstance.value;
  const startedAt = result?.evaluationStartedAt ?? result?.createdAtTimestamp;
  if (!metricId.value || !revision.value?.id || !startedAt || !instance?.tokens?.length) {
    error.value = 'This evaluated sentence is unavailable. Refresh the document and try again.';
    return;
  }
  if (!note.value.trim()) { error.value = 'Add a note explaining the incorrect suggestion.'; return; }
  saving.value = true;
  error.value = '';
  try {
    await $fetch(`/api/evaluation-metrics/${encodeURIComponent(metricId.value)}/flags`, {
      method: 'POST', headers: await useApiHeaders(), body: {
        path: `documents/${documentId}/revisions/${revision.value.id}/evaluationResults/${props.criteriaInstance.checklistCriteriaId}`,
        startedAt,
        rootTokenIds: instance.tokens.map(token => token.id),
        targetKind: props.targetKind, targetUid: props.targetUid, note: note.value.trim(),
      },
    });
    useBaseToast('Sentence flagged for metric review.', 'success');
    note.value = '';
    emit('saved');
  } catch (cause: any) {
    error.value = cause?.data?.statusMessage ?? 'Could not save this flag. Try again.';
  } finally { saving.value = false; }
};
</script>

<template>
  <form class="w-[min(22rem,calc(100vw-3rem))] space-y-3 p-3" @submit.prevent="save">
    <h3 class="text-body-1 font-semibold">Flag incorrect suggestion</h3>
    <p class="break-words text-small-1 text-medium">{{ targetLabel }}</p>
    <label v-if="!rootInstance && (candidates?.length ?? 0) > 1" class="block space-y-1 text-body-2">
      <span>Evaluated sentence</span>
      <select v-model="selectedIndex" class="w-full rounded border border-medium-tint bg-surface px-3 py-2 text-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus">
        <option value="" disabled>Select a sentence</option>
        <option v-for="(candidate, index) in candidates" :key="candidate.uid || index" :value="String(index)">#{{ index + 1 }} · {{ candidate.text || 'Evaluated sentence' }}</option>
      </select>
    </label>
    <p v-if="!selectedInstance" class="text-small-1 text-danger">No evaluated sentence is available for this flag.</p>
    <label class="block space-y-1 text-body-2">
      <span>What did this check suggest incorrectly?</span>
      <textarea v-model="note" maxlength="4000" rows="3" class="w-full rounded border border-medium-tint bg-surface px-3 py-2 text-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus" />
    </label>
    <p v-if="error" role="alert" class="text-small-1 text-danger">{{ error }}</p>
    <BaseButton type="submit" :disabled="saving || !selectedInstance" :show-spinner="saving">Save flag</BaseButton>
  </form>
</template>
